// --------------------------------------------------------------
// Created On: 2026-10-16
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment } from "react";
import { Active, closestCenter, DndContext, Over } from "@dnd-kit/core";
import DroppableArea from "src/components/DroppableArea/DroppableArea";
import SystemControlAttributeEditRow from "./SystemControlAttributeEditRow/SystemControlAttributeEditRow";
import formatDateShortLocal from "src/utilities/time/formatDateShortLocal";
import isoUtcIsStale from "src/utilities/time/isoUtcIsStale";
import calculateAttributeHighlightColor from "src/utilities/calculateAttributeHighlightColor";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import {
  GAUGE_COMPONENT_TYPE,
  GAUGE_TYPE_RADIAL,
  GAUGE_TYPE_SPEEDOMETER,
  GAUGE_TYPE_TANK_ROUND,
  GAUGE_TYPE_TANK_SQUARE,
  TABLE_COMPONENT_TYPE,
} from "src/constants/systemControl";
import styles from "./SystemControlComponentTable.module.scss";

// Table that contains a list of attributes, their values, and timestamps.
export default function SystemControlComponentTable(props: Props): Component {
  const MAX_ATTRIBUTES_FOR_COMPONENT = 10;
  // Gauges always show exactly one attribute, so attributes cannot be added, removed, or reordered.
  const isGauge = props.componentType === GAUGE_COMPONENT_TYPE;
  const gaugeUnits = props.selectedAttributes.at(0)?.unitShortName ?? "";
  // Gauges can only display numbers, so they only suggest numeric attributes.
  let attributeDatalistId = "datalist-attributes";
  if (isGauge) {
    attributeDatalistId = "datalist-attributes-numeric";
  }
  let gaugeUnitsLabel = "";
  if (gaugeUnits.length > 0) {
    gaugeUnitsLabel = ` (${gaugeUnits})`;
  }

  // Handles dropping an attribute row in a drop area, which moves it below the attribute at the index the area belongs
  // to. The drop area above the first attribute moves it to the top.
  function handleAttributeDragEnd(over: Over | null, active: Active): void {
    if (over !== null) {
      const fromIndex = parseInt(String(active.id).replace("attribute-", ""), 10);
      const belowIndex = over.id === "above-first" ? -1 : parseInt(String(over.id).replace("below-", ""), 10);
      if (!isNaN(fromIndex) && !isNaN(belowIndex)) {
        props.onReorderAttribute(fromIndex, belowIndex);
      }
    }
  }

  // Returns the color that highlights the row of an attribute based on its highlighting rules, or an empty string if
  // the row should not be highlighted.
  function getRowColor(selectedAttribute: ComponentAttribute): string {
    if (selectedAttribute.currentValue === null) {
      return "";
    }
    const color = calculateAttributeHighlightColor(
      selectedAttribute.highlightingRules,
      selectedAttribute.currentValue.value
    );
    if (color === null) {
      return "";
    } else {
      return color;
    }
  }

  // Returns the class names for the row of an attribute. Highlighted rows show their color across the whole row, and
  // stale rows that are highlighted also get stripes over their highlight color.
  function getRowClassName(selectedAttribute: ComponentAttribute): string {
    const classNames: string[] = [];
    const isStale = selectedAttribute.currentValue === null || isoUtcIsStale(selectedAttribute.currentValue.timestamp);
    const isHighlighted = getRowColor(selectedAttribute) !== "";
    if (isStale) {
      classNames.push(styles.staleData);
    }
    if (isHighlighted) {
      classNames.push(styles.highlighted);
    }
    if (isStale && isHighlighted) {
      classNames.push(styles.staleHighlighted);
    }
    return classNames.join(" ");
  }

  return (
    <div data-test="system-control-table" className={styles.body}>
      {/* Component type selection. */}
      {props.beingEdited && (
        <table className={`${styles.table} ${styles.editing} table mb-0 pb-0`}>
          <thead className="align-middle">
            <tr>
              <th className={styles.th}>Component Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select
                  data-test="component-type-select"
                  className="form-select"
                  value={props.componentType}
                  onChange={(e) => props.onChangeComponentType(e.target.value)}
                >
                  <option value={TABLE_COMPONENT_TYPE}>Table</option>
                  <option value={GAUGE_COMPONENT_TYPE}>Gauge</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Gauge type selection. */}
      {props.beingEdited && isGauge && (
        <table className={`${styles.table} ${styles.editing} table mb-0 pb-0`}>
          <thead className="align-middle">
            <tr>
              <th className={styles.th}>Gauge Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select
                  data-test="gauge-type-select"
                  className="form-select"
                  value={props.gaugeType}
                  onChange={(e) => props.onChangeGaugeType(e.target.value)}
                >
                  <option value={GAUGE_TYPE_TANK_ROUND}>Round Tank</option>
                  <option value={GAUGE_TYPE_TANK_SQUARE}>Square Tank</option>
                  <option value={GAUGE_TYPE_RADIAL}>Radial</option>
                  <option value={GAUGE_TYPE_SPEEDOMETER}>Speedometer</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Asset selection. */}
      {props.beingEdited && (
        <table className={`${styles.table} ${styles.sectionDivider} ${styles.editing} table mb-0 pb-0`}>
          <thead className="align-middle">
            <tr>
              <th className={styles.th}>Asset Name</th>
              <th className={`${styles.th} ${styles.shrinkCell}`}>
                Operation{" "}
                <IconTooltip
                  id="operation-tooltip"
                  icon="info-circle"
                  message="If this boxed is checked, then users can remotely operate this asset from this dashboard."
                  color="var(--info-tooltip)"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <input
                  data-test="component-asset-input"
                  className="form-control"
                  list="datalist-assets"
                  value={props.assetName}
                  onChange={(e) => props.onChangeAssetName(e.target.value)}
                />
              </td>
              <td className={styles.shrinkCell}>
                <input
                  data-test="component-asset-operation-allow"
                  className="form-check-input form-check-big"
                  type="checkbox"
                  checked={props.remoteOperationEnabled}
                  onChange={() => props.onToggleRemoteOperationEnabled()}
                />
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Attribute selection. */}
      {/* The dragged row is taller than the drop areas, so pick the area closest to its center instead of the first one
          that it overlaps. */}
      <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleAttributeDragEnd(e.over, e.active)}>
        <table
          className={`${styles.table} ${
            props.beingEdited ? `${styles.sectionDivider} ${styles.editing}` : ""
          } table mb-0 pb-0`}
        >
          <thead className="align-middle">
            <tr>
              {/* While editing a table, the heading spans the drag handle column so it lines up with the asset heading. */}
              <th className={styles.th} colSpan={props.beingEdited && !isGauge ? 2 : 1}>
                {props.beingEdited ? "Attribute Name" : "Name"}
              </th>
              {!props.beingEdited && <th className={styles.th}>Value</th>}
              {!props.beingEdited && <th className={styles.th}>Time</th>}
              {props.beingEdited && (
                <th className={styles.shrinkCell}>
                  {!isGauge && (
                    <button
                      data-test="add-attribute-button"
                      type="submit"
                      className="btn btn-success"
                      onClick={() => props.onAddAttribute()}
                      disabled={props.selectedAttributes.length >= MAX_ATTRIBUTES_FOR_COMPONENT}
                    >
                      <i className="fa fa-fw fa-plus fa-xs" />
                    </button>
                  )}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {props.beingEdited && !isGauge && (
              <tr className={styles.dropRow}>
                <td colSpan={3}>
                  <DroppableArea id="above-first" isSlim />
                </td>
              </tr>
            )}
            {props.selectedAttributes.map((selectedAttribute, i) => (
              <Fragment key={selectedAttribute.componentAssetAttributeId}>
                {props.beingEdited ? (
                  <SystemControlAttributeEditRow
                    index={i}
                    isFixed={isGauge}
                    datalistId={attributeDatalistId}
                    attributeName={selectedAttribute.attributeName}
                    units={selectedAttribute.unitShortName ?? ""}
                    isBoolean={
                      props.attributes.find((attribute) => attribute.attributeId === selectedAttribute.attributeId)
                        ?.isBoolean ?? false
                    }
                    isHighlightable={
                      props.attributes.find((attribute) => attribute.attributeId === selectedAttribute.attributeId)
                        ?.isHighlightable ?? false
                    }
                    regEnumId={selectedAttribute.regEnumId}
                    enumerationMap={props.enumerationMap}
                    highlightingRules={selectedAttribute.highlightingRules}
                    onChangeAttribute={(attributeName) =>
                      props.onChangeAttribute(selectedAttribute.componentAssetAttributeId, attributeName)
                    }
                    onChangeHighlightingRules={(highlightingRules) =>
                      props.onChangeHighlightingRules(selectedAttribute.componentAssetAttributeId, highlightingRules)
                    }
                    onRemoveAttribute={() => props.onRemoveAttribute(selectedAttribute.componentAssetAttributeId)}
                  />
                ) : (
                  <tr
                    className={getRowClassName(selectedAttribute)}
                    style={{ "--row-highlight-color": getRowColor(selectedAttribute) } as React.CSSProperties}
                  >
                    <td className={styles.nameCell}>
                      <span>{selectedAttribute.attributeName}</span>
                    </td>

                    <td className={styles.valueCell} data-test="system-control-table-value">
                      {selectedAttribute.currentValue === null || selectedAttribute.currentValue.value === null
                        ? "N/A"
                        : `${selectedAttribute.currentValue.value} ${
                            selectedAttribute.unitShortName === null ? "" : selectedAttribute.unitShortName
                          }`}
                    </td>

                    <td className={styles.timeCell}>
                      {selectedAttribute.currentValue === null || selectedAttribute.currentValue.timestamp === null
                        ? "N/A"
                        : formatDateShortLocal(selectedAttribute.currentValue.timestamp)}
                    </td>
                  </tr>
                )}
                {props.beingEdited && !isGauge && (
                  <tr className={styles.dropRow}>
                    <td colSpan={3}>
                      <DroppableArea id={`below-${i}`} isSlim />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </DndContext>

      {/* Gauge range selection. */}
      {props.beingEdited && isGauge && (
        <table className={`${styles.table} ${styles.sectionDivider} ${styles.editing} table mb-0 pb-0`}>
          <thead className="align-middle">
            <tr>
              <th className={styles.th}>Min{gaugeUnitsLabel}</th>
              <th className={styles.th}>Max{gaugeUnitsLabel}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <input
                  data-test="component-gauge-min-input"
                  className="form-control"
                  type="number"
                  step="any"
                  value={props.gaugeMin}
                  onChange={(e) => props.onChangeGaugeMin(e.target.value)}
                />
              </td>
              <td>
                <input
                  data-test="component-gauge-max-input"
                  className="form-control"
                  type="number"
                  step="any"
                  value={props.gaugeMax}
                  onChange={(e) => props.onChangeGaugeMax(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

interface Props {
  componentType: string;
  gaugeType: string;
  gaugeMin: string;
  gaugeMax: string;
  assets: Asset[];
  attributes: Attribute[];
  enumerationMap: EnumerationMap;
  assetName: string;
  remoteOperationEnabled: boolean;
  selectedAttributes: ComponentAttribute[];
  beingEdited: boolean;
  onChangeComponentType: (componentType: string) => void;
  onChangeGaugeType: (gaugeType: string) => void;
  onChangeGaugeMin: (min: string) => void;
  onChangeGaugeMax: (max: string) => void;
  onChangeAssetName: (assetName: string) => void;
  onToggleRemoteOperationEnabled: () => void;
  onChangeAttribute: (componentAssetAttributeId: number, attributeName: string) => void;
  onChangeHighlightingRules: (componentAssetAttributeId: number, highlightingRules: HighlightingRule[]) => void;
  onRemoveAttribute: (componentAssetAttributeId: number) => void;
  onAddAttribute: () => void;
  onReorderAttribute: (fromIndex: number, belowIndex: number) => void;
}

interface Attribute {
  attributeId: number;
  regEnumId: number | null;
  name: string;
  code: string;
  unitShortName: string;
  unitLongName: string;
  isBoolean: boolean;
  isHighlightable: boolean;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface ComponentAttribute {
  componentAssetAttributeId: number;
  attributeId: number;
  attributeName: string;
  assetId: number;
  assetName: string;
  unitShortName: string | null;
  unitLongName: string | null;
  regEnumId: number | null;
  currentValue: AttributeValue | null;
  graphValues: AttributeValue[];
  sortPriority: number;
  highlightingRules: HighlightingRule[];
}

interface AttributeValue {
  value: string | null;
  timestamp: string | null;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}

interface Asset {
  assetId: number;
  assetName: string;
  assetNickname: string;
  controllerModel: string | null;
  controllerName: string | null;
  deviceModel: string | null;
  deviceIdentifier: string | null;
  digitalOutputControls: DigitalOutputControl[];
  remoteOperationIsLocked: boolean;
  remoteOperationAllowed: boolean;
}

interface DigitalOutputControl {
  position: number;
  operationLabel: string;
  labelWhenTrue: string;
  labelWhenFalse: string;
}
